import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, ArrowUpRight, ArrowLeft } from 'lucide-react'
import AccountMenu from './AccountMenu'
import DiscordIcon from '@/components/site/DiscordIcon'
import TopoBackground from '@/components/TopoBackground'
import useDiscordInvite from '@/components/site/useDiscordInvite'
import { useLanguage } from '@/contexts/LanguageContext'
import { HOME_LOGIN_URL, useHomeAuth } from '@/contexts/HomeAuthContext'
import { type Language } from '@/i18n/translations'
import { JOIN_SITE_URL, DASHBOARD_SITE_URL } from '@/lib/site'
import { SUPPORTER_ENABLED } from '@/lib/features'

/**
 * Page chrome for turksletloose.com — the same photograph, scrim and graphite
 * surfaces as join.turksletloose.com, so the two public sites read as one
 * property. Nothing here touches AuthContext: these are anonymous visitors.
 *
 * The bar differs from the join site's in one way that matters: its primary
 * action leaves for the recruitment hostname instead of starting an OAuth flow,
 * because applications do not live on this domain.
 *
 * `sectionNav` adds the landing page's own anchor links; another page passes
 * its own through `sections` (the supporter page does). `pageTitle` renames the
 * tab per page. The calculator and supporter links sit after those in every
 * page's bar — they are routes, not anchors of the landing page. The supporter
 * one is off entirely while `SUPPORTER_ENABLED` is false, in the bar and in the
 * footer both: a link to a route that redirects away is worse than no link.
 *
 * The way back to the landing page is *not* in the bar: on a sub-page the bar
 * carries that page's own sections, and a "Home" entry among them reads as one
 * more section of it. It sits on its own row under the bar instead, with a back
 * arrow, where it reads as leaving the page rather than as part of it.
 */
export default function HomeShell({
  children,
  sectionNav = false,
  sections: pageSections,
  pageTitle,
}: {
  children: ReactNode
  sectionNav?: boolean
  sections?: { href: string; label: string }[]
  pageTitle?: string
}) {
  const { lang, setLang, T } = useLanguage()
  const copyright = T.join.copyright.replace('{year}', String(new Date().getFullYear()))
  const path = useLocation().pathname
  const onSupporter = path === '/supporter'
  const onCalculator = path === '/calculator'

  // index.html is shared with the dashboard, so its <title> is the dashboard's.
  // The public site renames the tab for itself, and follows the language switch.
  useEffect(() => {
    document.title = pageTitle
      ? `${pageTitle} · ${T.join.brand}`
      : T.home.pageTitle
  }, [T, pageTitle])

  const inviteUrl = useDiscordInvite()
  const { user, loading } = useHomeAuth()

  // The bar is not pinned over the hero: it scrolls away with the page, then
  // drops back down once you are past the fold.
  const [pinned, setPinned] = useState(false)
  useEffect(() => {
    const onScroll = () => setPinned(window.scrollY > 220)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Deliberately not the section headings: those are full sentences ("Our game
  // server", "What we play") and wrapped onto a second line inside the 56px bar,
  // squeezing the wordmark. The nav carries its own one-word labels instead.
  const sections = sectionNav
    ? [
        { href: '#community',   label: T.home.navCommunity },
        { href: '#games',       label: T.home.navGames },
        { href: '#competitive', label: T.home.navCompetitive },
      ]
    : pageSections ?? []

  const navLinkClass =
    'whitespace-nowrap font-display text-[13px] font-semibold tracking-tight ' +
    'text-foreground/70 transition-colors hover:text-foreground'

  const bar = (
    <div className="mx-auto flex h-[68px] w-full max-w-6xl items-center gap-3 px-5">
      <Link to="/" className="flex min-w-0 items-center gap-3">
        <img src="/tll_logo.png" alt="" className="h-10 w-10 rounded-md object-contain" />
        <div className="min-w-0">
          <p className="font-display text-sm font-bold uppercase leading-none tracking-tight">
            {T.join.brand}
          </p>
          <p className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-muted-foreground">
            {T.home.tagline}
          </p>
        </div>
      </Link>

      {/* Havan hesabı ve Destekçi Üyeliği gezinmede. İkisi de bölüm
          çapalarının sonuna gelir çünkü onlar gibi çapa değil, ayrı birer
          rotadır. Bulunulan sayfaya link basılmaz: /calculator ya da
          /supporter üzerindeyken çubukta yalnızca o sayfanın kendi bölümleri
          kalır, ana sayfa linki çubuğun altındaki satırdadır. */}
      <nav className="ml-6 hidden shrink-0 items-center gap-5 lg:flex">
        {sections.map(sec => (
          <a
            key={sec.href}
            href={sec.href}
            className={navLinkClass}
          >
            {sec.label}
          </a>
        ))}
        {!onCalculator && (
          <Link to="/calculator" className={navLinkClass}>{T.calculator.navLabel}</Link>
        )}
        {SUPPORTER_ENABLED && !onSupporter && (
          <Link to="/supporter" className={navLinkClass}>{T.home.navSupporter}</Link>
        )}
      </nav>

      <div className="ml-auto flex items-center gap-2">
        <a
          href={JOIN_SITE_URL}
          className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm bg-primary px-3 py-1.5 text-[11px] font-bold tracking-wider text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex"
        >
          {T.home.joinCta}
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>

        {/* Signed-in visitors get their avatar; everyone else the sign-in door.
            Rendered only once /api/home/me has answered, so the bar does not
            flicker from "sign in" to an avatar on every page load. */}
        {!loading && (user ? (
          <AccountMenu user={user} />
        ) : (
          <a
            href={HOME_LOGIN_URL}
            title={T.profile.signIn}
            className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-sm border border-white/12 bg-white/5 px-3 py-1.5 text-[11px] font-bold tracking-wider text-foreground/80 transition-colors hover:bg-white/10 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <DiscordIcon size={14} />
            {T.profile.navSignIn}
          </a>
        ))}

        <div className="relative">
          <select
            value={lang}
            onChange={e => setLang(e.target.value as Language)}
            aria-label={T.language.select}
            className="cursor-pointer appearance-none rounded-sm border border-white/12 bg-white/5 py-1.5 pl-3 pr-7 text-[11px] font-bold tracking-wider text-foreground/80 transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="en">EN</option>
            <option value="tr">TR</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>
    </div>
  )

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0b0b0d]">
      {/* Page surface: procedurally drawn contour terrain, fixed, so the whole
          page scrolls over one continuous map. This is not the graph-paper grid
          that used to live here — that one ruled straight lines behind straight
          columns of text and fought them. Contours run against the layout
          instead of with it, and at this alpha they read as ground rather than
          as a pattern. The hero covers it with its own photograph; every
          section below is translucent so it shows through.

          The film grain that used to sit on top is gone: a full-screen
          fractalNoise at 0.18 crawls on any panel that is not flat black, and
          it was sanding the contour lines down to a speckle. The terrain is the
          texture now. */}
      <div className="pointer-events-none fixed inset-0">
        <TopoBackground />
      </div>

      <div className="relative flex min-h-screen flex-col">
        {/* Two copies of the same bar: one in normal flow that scrolls away with
            the hero, one fixed copy that drops back down past the fold. A single
            fixed-and-hidden bar would leave the logo and switcher unreachable at
            the top of the page. */}
        <header className="relative z-20 border-b border-white/10 bg-black/25 backdrop-blur-sm">
          {bar}
        </header>

        <header
          className={`fixed inset-x-0 top-0 z-30 border-b border-white/10 bg-black/70 backdrop-blur-md
                      transition-transform duration-300 ease-out
                      ${pinned ? 'translate-y-0' : '-translate-y-full'}`}
          aria-hidden={!pinned}
        >
          {bar}
        </header>

        {!sectionNav && (
          <div className="relative z-10 mx-auto w-full max-w-6xl px-5 pt-4">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 font-display text-[12px] font-semibold tracking-tight text-foreground/60 transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {T.supporter.backHome}
            </Link>
          </div>
        )}

        <main className="flex-1">{children}</main>

        <footer className="border-t border-white/10 bg-black/40 backdrop-blur-md">
          <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-3 px-5 py-8 text-center">
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
              <a
                href={JOIN_SITE_URL}
                className="font-display text-[12px] font-semibold tracking-tight text-foreground/70 transition-colors hover:text-foreground"
              >
                {T.home.joinCta}
              </a>
              {inviteUrl && (
                <a
                  href={inviteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-display text-[12px] font-semibold tracking-tight text-foreground/70 transition-colors hover:text-foreground"
                >
                  {T.join.discordCta}
                </a>
              )}
              <Link
                to="/calculator"
                className="font-display text-[12px] font-semibold tracking-tight text-foreground/70 transition-colors hover:text-foreground"
              >
                {T.calculator.navLabel}
              </Link>
              {SUPPORTER_ENABLED && (
                <Link
                  to="/supporter"
                  className="font-display text-[12px] font-semibold tracking-tight text-foreground/70 transition-colors hover:text-foreground"
                >
                  {T.supporter.navLabel}
                </Link>
              )}
              <a
                href={DASHBOARD_SITE_URL}
                className="font-display text-[12px] font-semibold tracking-tight text-foreground/70 transition-colors hover:text-foreground"
              >
                {T.home.dashboardLink}
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground/70">{copyright}</p>
            <p className="text-[11px] text-muted-foreground/50">{T.home.footerNote}</p>
          </div>
        </footer>
      </div>
    </div>
  )
}
